import { addDays, isAfter, isBefore } from 'date-fns';
import {
  TransactionChargeDto,
  TransactionChargesResponseDto,
} from '@tiendanube/common';
import {
  Status as PaymentStatusEnum,
  Country,
} from '@tiendanube/common/src/enums';
import { PAGO_NUBE_PROVIDER_NAME } from 'commons/constants';
import { useGetCountry } from '../../../../../Auth/hooks';
import { useNuvemPagoRedirect } from '../useNuvemPagoRedirect';

export interface Result {
  hasMerchantCharges: () => boolean;
  hasRefundedMerchantCharges: () => boolean;
  shouldShowMerchantCharges: () => boolean;
  isMerchantChargeInProgress: (paymentStatus: PaymentStatusEnum) => boolean;
}

// TODO: these constants are provisional and can be removed once the launch date
// for Pago Nube merchant charges is set and at least 2 days have passed since then
const pnMerchantChargesLaunchDate = new Date('2025-04-01T00:00:00Z');
const daysToWaitForMerchantCharge = 2;

function useShowMerchantCharges(
  charges?: TransactionChargesResponseDto,
  gateway?: string,
  paymentCreationDate?: string,
): Result {
  const country = useGetCountry();
  const { isNuvemPagoBrazilPayment, isNuvemPagoCommonsPayment } =
    useNuvemPagoRedirect(gateway ?? '');

  return {
    hasMerchantCharges: () => (charges?.merchantCharges?.length ?? 0) > 0,
    hasRefundedMerchantCharges: () =>
      (charges?.refundedMerchantCharges?.length ?? 0) > 0,
    shouldShowMerchantCharges: () =>
      isNuvemPagoBrazilPayment() || isNuvemPagoCommonsPayment(),
    isMerchantChargeInProgress: (paymentStatus: PaymentStatusEnum) => {
      if (
        ![
          PaymentStatusEnum.PAID,
          PaymentStatusEnum.REFUNDED,
          PaymentStatusEnum.PARTIALLY_REFUNDED,
        ].includes(paymentStatus)
      ) {
        return false;
      }
      return isMerchantChargeInProgress(
        country,
        charges?.merchantCharges,
        gateway,
        paymentCreationDate,
      );
    },
  };
}

function isMerchantChargeInProgress(
  country: Country,
  merchantCharges?: TransactionChargeDto[],
  gateway?: string,
  paymentCreationDate?: string,
): boolean {
  if (gateway !== PAGO_NUBE_PROVIDER_NAME) {
    return false;
  }
  if (country !== Country.AR) {
    return false;
  }

  const isWaitingForMerchantCharge: boolean = paymentCreationDate
    ? isBefore(
        new Date(),
        addDays(new Date(paymentCreationDate), daysToWaitForMerchantCharge),
      )
    : false;

  // TODO: The following constant can be removed once the launch date for Pago Nube merchant charges is set
  // and the 2-day waiting period (value of daysToWaitForMerchantCharge) has passed
  const isPNReadyForMerchantCharge: boolean = isAfter(
    new Date(),
    pnMerchantChargesLaunchDate,
  );
  return (
    (merchantCharges?.length ?? 0) === 0 &&
    isWaitingForMerchantCharge &&
    isPNReadyForMerchantCharge
  );
}

export default useShowMerchantCharges;
